/** Layout, tooltip, and label helpers for trend-curve charts (no D3). */

import {
  TREND_CURVE_LEGEND_MAX_ITEMS,
  TREND_CURVE_MIN_CHART_WIDTH_COMPACT,
  TREND_CURVE_MIN_CHART_WIDTH_EXPANDED,
  TREND_CURVE_PX_PER_PERIOD_COMPACT,
  TREND_CURVE_PX_PER_PERIOD_EXPANDED,
  TREND_CURVE_WIDTH_MARGIN_COMPACT,
  TREND_CURVE_WIDTH_MARGIN_EXPANDED,
} from "../../constants/trendCurveConstants";

export const formatTrendCurveContribution = (value: number) =>
  `${Number(value).toFixed(2)}%`;

export const truncateTrendCurveLegendLabel = (
  label: string,
  maxChars: number
) => {
  const trimmed = label.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
};

export const getTrendCurveLegendItems = <
  T extends { label: string; tNo?: number; borderColor: string },
>(
  datasets: T[],
  maxItems = TREND_CURVE_LEGEND_MAX_ITEMS
) => datasets.slice(0, maxItems);

export const buildTrendCurveTooltipHtml = ({
  trendName,
  tNo,
  period,
  contribution,
  color,
  expanded,
}: {
  trendName: string;
  tNo?: number;
  period: string;
  contribution: number;
  color: string;
  expanded: boolean;
}) => {
  const fontSize = expanded ? "13px" : "11px";
  const titleSize = expanded ? "14px" : "12px";

  return `
    <div style="
      padding: 10px 12px;
      background: #f4f6f5;
      border: 2px solid #c5cdc7;
      border-radius: 6px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.12);
      min-width: 160px;
      font-family: Poppins, sans-serif;
      font-size: ${fontSize};
      color: #1f2937;
      line-height: 1.5;
    ">
      <div style="
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 8px;
        padding-bottom: 6px;
        border-bottom: 1px solid #d1d5db;
      ">
        <span style="color: ${color}; font-size: 16px; line-height: 1;">●</span>
        <span style="font-weight: 600; font-size: ${titleSize}; color: #111827;">
          ${trendName}
        </span>
      </div>
      ${tNo != null ? `<div><span style="color: #6b7280;">Trend No.</span> <strong>${tNo}</strong></div>` : ""}
      <div><span style="color: #6b7280;">Period</span> <strong>${period}</strong></div>
      <div><span style="color: #6b7280;">Contribution</span> <strong>${formatTrendCurveContribution(contribution)}</strong></div>
    </div>
  `;
};

export const computeTrendCurveYAxisMax = (values: number[]) => {
  const maxValue = Math.max(...values, 0);
  return maxValue <= 0
    ? 10
    : Math.min(100, Math.ceil((maxValue * 1.15) / 5) * 5);
};

export const computeTrendCurveXPadding = (labelCount: number) => {
  if (labelCount <= 2) return 0.05;
  if (labelCount <= 4) return 0.12;
  if (labelCount <= 6) return 0.18;
  if (labelCount <= 8) return 0.25;
  return 0.35;
};

/** Min share of available width so sparse charts stay a moderate size (not a thin strip). */
export const computeTrendCurveMinWidthFraction = (labelCount: number) => {
  const n = Math.max(labelCount, 1);
  if (n <= 1) return 0.55;
  if (n <= 2) return 0.6;
  if (n <= 4) return 0.68;
  if (n <= 6) return 0.82;
  if (n <= 8) return 0.92;
  return 1;
};

/**
 * Chart SVG width from period count, capped at available container width.
 * Few months → moderate shrink (~55–75% for 2–4 periods); many months fill.
 */
export const computeTrendCurvePreferredChartWidth = (
  labelCount: number,
  expanded: boolean,
  availableWidth?: number
) => {
  const n = Math.max(labelCount, 1);
  const perPeriod = expanded
    ? TREND_CURVE_PX_PER_PERIOD_EXPANDED
    : TREND_CURVE_PX_PER_PERIOD_COMPACT;
  const margin = expanded
    ? TREND_CURVE_WIDTH_MARGIN_EXPANDED
    : TREND_CURVE_WIDTH_MARGIN_COMPACT;
  const absoluteMin = expanded
    ? TREND_CURVE_MIN_CHART_WIDTH_EXPANDED
    : TREND_CURVE_MIN_CHART_WIDTH_COMPACT;
  const contentPreferred = Math.max(absoluteMin, n * perPeriod + margin);

  if (availableWidth == null || availableWidth <= 0) {
    return contentPreferred;
  }

  const fractionFloor = availableWidth * computeTrendCurveMinWidthFraction(n);
  const preferred = Math.max(contentPreferred, fractionFloor);
  return Math.min(availableWidth, preferred);
};

/** Prefer upright month labels when there is enough horizontal room. */
export const shouldRotateTrendCurveXLabels = (
  labels: string[],
  chartWidth: number
) => {
  const labelCount = labels.length;
  if (labelCount === 0) return false;
  if (labelCount <= 6 && chartWidth >= 420) return false;

  const avgSlot = chartWidth / labelCount;
  const longest = Math.max(...labels.map(label => label.length), 0);
  // ~6.5px per character at ~12px Poppins
  return avgSlot < longest * 6.5 + 8;
};

export const computeTrendCurveBottomMargin = (
  labels: string[],
  chartWidth: number
) => {
  // +18 reserves a band for the "Time Period" X-axis label below the ticks.
  if (!shouldRotateTrendCurveXLabels(labels, chartWidth)) {
    return 52;
  }

  const hasLongLabels = labels.some(label => label.length > 10);
  const labelCount = labels.length;

  // Detect quarter/half-year formats which have wider labels when rotated
  // Quarter: "Q1 2026" or "Jan–Mar 2026"
  // Half-year: "Jan–Jun 2026" / "Jul–Dec 2026"
  const hasQuarterLabels = labels.some(
    label => /^Q\d\s+\d{4}$/.test(label) || label.includes("–")
  );

  // Base margin for rotated labels (accounts for tick label height at -40deg rotation)
  // The "Time Period" label is placed at chartHeight + 64, so we need at least ~70px
  // for the rotated tick labels + "Time Period" label clearance.
  let baseMargin = 76;

  // Quarter/half-year labels need more vertical space when rotated due to wider format
  if (hasQuarterLabels) {
    baseMargin += 36;
  }

  // Increase margin for long labels (they extend further when rotated)
  if (hasLongLabels) {
    baseMargin = Math.max(baseMargin, chartWidth < 600 ? 104 : 98);
  }

  // Increase margin for many labels (more labels = more vertical extent when rotated)
  if (labelCount > 12) {
    baseMargin += 16;
  } else if (labelCount > 8) {
    baseMargin += 8;
  }

  return baseMargin;
};

export const getTrendCurveXAxisTickLabels = (
  labels: string[],
  chartWidth: number
): string[] => {
  if (chartWidth >= 700 || labels.length <= 8) {
    return labels;
  }

  if (labels.length <= 12) {
    return labels;
  }

  return labels.filter(
    (_, index) => index % 2 === 0 || index === labels.length - 1
  );
};

/** Single-series contribution % label layout (font, offset, top margin, skip step). */
export const computeTrendCurveContributionLabelLayout = (
  labelCount: number,
  chartWidth: number,
  expanded: boolean
) => {
  // Sparse / normal: large for screenshot readability
  let fontSizePx = expanded ? 26 : 24;
  let offsetY = expanded ? 22 : 21;
  let topMargin = expanded ? 54 : 50;
  let labelStep = 1;

  if (labelCount <= 0) {
    return { fontSizePx, offsetY, topMargin, labelStep };
  }

  // scalePoint spacing ≈ chartWidth / n (padding shrinks usable range further)
  const avgSlot = chartWidth / labelCount;
  // "~NN.NN%" ≈ 7 glyphs; bold Poppins ~0.62em per glyph
  const estimatedLabelWidth = fontSizePx * 0.62 * 7;

  if (labelCount > 10 || avgSlot < estimatedLabelWidth * 0.85) {
    // Dense: still bumped, but smaller + every other label
    fontSizePx = expanded ? 22 : 20;
    offsetY = expanded ? 18 : 17;
    topMargin = expanded ? 46 : 42;
    labelStep = 2;
  } else if (labelCount > 6 || avgSlot < estimatedLabelWidth) {
    // Moderate density: keep large fonts, thin labels to avoid collisions
    labelStep = 2;
  }

  return { fontSizePx, offsetY, topMargin, labelStep };
};

export const shouldShowTrendCurveContributionLabel = (
  index: number,
  labelCount: number,
  labelStep: number
) => {
  if (labelStep <= 1) return true;
  return index % labelStep === 0 || index === labelCount - 1;
};
